<template>
    <Modal
        ref="modalRef"
        :primary-button-disabled="isSaveDisabled"
        :primary-button-label="trans('general.action.save')"
        :processing="form.processing"
        max-width="2xl"
        @submit="submitForm"
    >
        <template #title>
            <div class="-mx-8 -my-4 bg-[#002377] px-8 py-4 text-white">
                <asset-short-info
                    v-if="workOrder.asset"
                    :asset="workOrder.asset"
                    :clickable="!isDraft"
                    @asset-details-visited="!isDraft && modalRef?.close()"
                >
                    <template #top>
                        <status-badge :enable-mobile="true" :operability="workOrder.asset?.status?.operability" />
                    </template>
                </asset-short-info>
            </div>
        </template>
        <div v-if="!!error">
            <alert color="danger">
                {{ error }}
            </alert>
        </div>
        <div v-else-if="hasWorkOrder">
            <div class="mb-11 flex flex-wrap items-center gap-3">
                <div class="flex min-w-0 flex-1 items-center gap-3">
                    <work-order-tag />
                    <div class="min-w-0 truncate text-[#002377] underline">{{ workOrder?.code }}</div>
                </div>
                <div class="flex w-full flex-wrap items-center justify-end gap-3 sm:ml-auto sm:w-auto">
                    <jet-select
                        v-if="priorityEnabled"
                        v-model="form.priority"
                        :disabled="(priorities?.data || []).length === 0"
                        :horizontal="false"
                        :inline="true"
                        :invalid-feedback="form.errors.priority"
                        :options="priorities?.data || []"
                        :readonly="!fieldsEditable || priorities.length === 0"
                        :required="true"
                        class="!mb-0 h-8 rounded-[8px] border-1 px-2 py-1"
                        name="priority"
                    >
                        <template #readonly>
                            <span v-if="workOrder">
                                {{ workOrder?.priority.label }}
                            </span>
                            <span v-else>{{ trans('general.none') }}</span>
                        </template>
                    </jet-select>

                    <jet-select
                        v-if="form.status !== 'draft'"
                        v-model="form.status"
                        :disabled="(statuses?.data?.length ?? 0) < 2"
                        :horizontal="false"
                        :inline="true"
                        :invalid-feedback="form.errors.status"
                        :options="statuses?.data || []"
                        :readonly="!(fieldsEditable || canReopen) || (statuses?.data?.length ?? 0) < 2"
                        :required="true"
                        class="!mb-0 h-8 rounded-[8px] border-1 px-2 py-1"
                        name="status"
                    >
                        <template #readonly>
                            {{ workOrder?.status?.label || trans('general.none') }}
                        </template>
                    </jet-select>
                </div>
            </div>
            <inoperability-reminder-checkbox
                v-if="inoperableReminderVisible"
                v-model="form.metadata.inoperability_tag_acknowledged"
                :reminder-key="inoperableReminderKey"
                id="inoperability-tag-acknowledged-edit"
                class="-mt-7 mb-4 !ps-1"
            />
            <step-progress v-if="processSteps?.length > 0" :steps="processSteps" class="my-5" />
            <div :class="isMobile ? 'ps-7' : 'ps-3'">
                <collapsable class="mb-7">
                    <template #toggler>
                        <h3 class="mb-1 !leading-[22px] !font-bold">
                            <inline-editable-field
                                v-model="form.title"
                                :editable="fieldsEditable"
                                :invalid-feedback="form.errors.title"
                                :line-height="24"
                                :placeholder="trans('workorder.form.title.placeholder')"
                                name="title"
                            />
                        </h3>
                    </template>

                    <inline-editable-field
                        v-model="form.description"
                        :editable="fieldsEditable"
                        :invalid-feedback="form.errors.description"
                        :max-height="220"
                        :placeholder="trans('workorder.form.description.placeholder')"
                        :rows="2"
                        class="mb-4"
                        name="description"
                    />

                    <case-category-section
                        v-if="catsaExtrasEnabled"
                        v-model:category="form.metadata.case_category"
                        v-model:subcategory="form.metadata.case_subcategory"
                        :asset-type="workOrder.asset?.type?.value"
                        :categories="caseCategories"
                        :category-readonly="!isCaseCategoryEditable"
                        :readonly="!fieldsEditable"
                        class="mb-5"
                    />

                    <troubleshooting-section
                        v-if="troubleshootingEnabled"
                        v-model="form.metadata.troubleshooting"
                        :readonly="!fieldsEditable"
                        class="mb-5"
                    />

                    <msd-notification-section
                        v-if="msdNotificationEnabled"
                        v-model="form.metadata.msd_notification"
                        :readonly="!fieldsEditable"
                        class="mb-5"
                    />

                    <impact-assessment-section
                        v-if="impactAssessmentEnabled"
                        v-model="form.metadata.impact_assessment"
                        :readonly="!fieldsEditable"
                        class="mb-5"
                    />

                    <work-order-info
                        :assignees="groupedAssignees || []"
                        :catsa-extras="catsaExtras"
                        :escalation-info="escalationInfo"
                        :form="form"
                        :is-assignee-required="isAssigneeRequired"
                        :is-editable="fieldsEditable"
                        :work-order="workOrder"
                        @escalate="onEscalate"
                    />

                    <dl class="mb-0">
                        <div v-if="workOrder.sourceTask" class="row">
                            <dt class="text-truncate col-sm-4 col-12 text-[14px] !font-medium">
                                {{ trans('workorder.details.workorder.source_task.label') }}
                            </dt>
                            <dd class="col-sm-8 col-12 text-muted">
                                <a class="text-[#002377] underline" href="#" @click.prevent="openTask(workOrder.sourceTask.url)">
                                    {{ workOrder.sourceTask.code }}
                                </a>
                            </dd>
                        </div>
                        <div class="row">
                            <dt class="text-truncate col-sm-4 col-12 text-[14px] !font-medium">
                                {{
                                    trans(
                                        catsaExtrasEnabled
                                            ? 'workorder.details.workorder.case_id.label'
                                            : 'workorder.details.workorder.ticket_id.label',
                                    )
                                }}
                            </dt>
                            <dd class="col-sm-8 col-12 text-muted">
                                <inline-editable-field
                                    v-model="form.ticketId"
                                    :editable="fieldsEditable"
                                    :invalid-feedback="form.errors.ticketId"
                                    :line-height="22"
                                    :placeholder="
                                        trans(catsaExtrasEnabled ? 'workorder.form.case_id.placeholder' : 'workorder.form.ticket_id.placeholder')
                                    "
                                    :resizable="false"
                                />
                            </dd>
                        </div>
                        <div class="row">
                            <dt class="text-truncate col-sm-4 col-12 text-[14px] !font-medium">
                                {{ trans('workorder.details.workorder.ticket_link.label') }}
                            </dt>
                            <dd class="col-sm-8 col-12 text-muted">
                                <inline-editable-field
                                    v-model="form.ticketLink"
                                    :editable="fieldsEditable"
                                    :invalid-feedback="form.errors.ticketLink"
                                    :line-height="22"
                                    :placeholder="trans('workorder.form.ticket_link.placeholder')"
                                    :resizable="false"
                                />
                            </dd>
                        </div>
                        <Deferred v-if="externalTicketsEnabled" data="externalTickets">
                            <external-tickets-badges :tickets="externalTickets" />
                            <template #fallback>
                                <div class="row">
                                    <dt class="text-truncate col-sm-4 col-12 text-[14px] !font-medium">
                                        {{ trans('workorder.details.workorder.external_tickets.label') }}
                                    </dt>
                                    <dd class="col-sm-8 col-12 mb-0 text-muted">
                                        <spinner />
                                    </dd>
                                </div>
                            </template>
                        </Deferred>
                    </dl>
                </collapsable>

                <collapsable class="mb-7">
                    <template #toggler>
                        <h3 class="mb-1 text-xl !leading-[20px] !font-bold">
                            {{ trans('workorder.details.section.attachments.title') }}
                            <em v-if="attachmentsRequired" class="text-danger">*</em>
                        </h3>
                    </template>
                    <!-- A done work order only accepts additions — existing attachments can no longer be renamed or deleted. -->
                    <file-list
                        :attachments="visibleAttachments"
                        :can-delete="hasAnyPermission('workorder.attachment.delete') && !isDone"
                        :can-edit="hasAnyPermission('workorder.attachment.update') && !isDone"
                        :can-read="hasAnyPermission(['workorder.attachment.index', 'workorder.attachment.read'])"
                        :readonly="false"
                        class="mb-3"
                        @deleted="handleDeletedAttachment"
                        @updated="reloadWorkOrder()"
                    />

                    <file-upload
                        v-if="isEditable"
                        v-model="form.attachments"
                        :accepted-file-types="attachmentUiConfig.allowedFileTypes"
                        :class="{ 'is-invalid': form.errors.attachments }"
                        :drop-area-hint="attachmentUiConfig.dropAreaHint"
                        :drop-area-label="attachmentUiConfig.dropAreaLabel"
                        @upload-started="uploadInProgress = true"
                        @upload-completed="uploadInProgress = false"
                    />
                    <div class="invalid-feedback">
                        {{ form.errors.attachments }}
                    </div>
                </collapsable>

                <collapsable v-if="workOrder" class="mb-4">
                    <template #toggler>
                        <h3 class="mb-4 text-xl !leading-[20px] !font-bold">
                            {{ trans('workorder.details.section.comments.title') }}
                        </h3>
                    </template>
                    <comments
                        :create-comment-url="
                            route('workorders.comments.store', {
                                workorder: workOrder.id,
                            })
                        "
                        :editable="true"
                        :get-comments-url="
                            route('workorders.comments.index', {
                                workorder: workOrder,
                            })
                        "
                        :initial-comments="workOrder?.comments"
                        :placeholder="trans('workorder.details.section.comments.placeholder')"
                        :poll-interval="15000"
                        :show-warning="showCommentWarning"
                        @input="updateComment"
                    />
                </collapsable>

                <div :class="isMobile ? '-me-4 pe-4' : '-me-8 pe-8'" class="-ms-11 bg-[#F8F8F8] py-4 ps-11">
                    <work-order-activity-log :workorder-history="workOrderHistory" />
                </div>
            </div>
        </div>

        <template #footer>
            <!-- Completed WOs are view-only: Save appears when an attachment is staged, or when a
                 privileged user is reopening the work order (status changed). -->
            <template v-if="isEditable && isDone">
                <jet-button
                    :disabled="uploadInProgress"
                    style="flex: 1"
                    type="button"
                    variant="secondary"
                    @click.stop="ensureCommentPosted(closeModal)"
                    >{{ trans('general.action.close') }}
                </jet-button>
                <jet-button
                    v-if="!error && (form.attachments?.length || statusChanged)"
                    :disabled="isSaveDisabled"
                    :is-processing="form.processing"
                    color="primary"
                    style="flex: 1"
                    @click.stop="ensureCommentPosted(submitForm)"
                >
                    {{ trans('general.action.save') }}
                </jet-button>
            </template>
            <template v-else>
                <jet-button
                    v-if="isEditable"
                    :disabled="uploadInProgress"
                    style="flex: 1"
                    type="button"
                    variant="secondary"
                    @click.stop="ensureCommentPosted(closeModal)"
                    >{{ trans('general.action.cancel') }}
                </jet-button>
                <jet-button
                    v-else
                    :disabled="uploadInProgress"
                    style="flex: 1"
                    type="button"
                    variant="secondary"
                    @click.stop="ensureCommentPosted(closeModal)"
                    >{{ trans('general.action.close') }}
                </jet-button>
                <jet-button
                    v-if="!error && isEditable"
                    :disabled="isSaveDisabled"
                    :is-processing="form.processing"
                    color="primary"
                    style="flex: 1"
                    @click.stop="ensureCommentPosted(submitForm)"
                >
                    {{ trans('general.action.save') }}
                </jet-button>
            </template>
        </template>
    </Modal>

    <resolve-preventative-work-order-modal ref="resolvePreventativeModalRef" name="resolve-preventative-details" />
</template>

<script setup>
import JetButton from '@/Jetstream/Button.vue';
import WorkOrderInfo from '@/Pages/WorkOrders/Modals/WorkOrderInfo.vue';
import ImpactAssessmentSection from '@/Pages/WorkOrders/Modals/ImpactAssessmentSection.vue';
import MsdNotificationSection from '@/Pages/WorkOrders/Modals/MsdNotificationSection.vue';
import CaseCategorySection from '@/Pages/WorkOrders/Modals/CaseCategorySection.vue';
import TroubleshootingSection from '@/Pages/WorkOrders/Modals/TroubleshootingSection.vue';
import ResolvePreventativeWorkOrderModal from '@/Pages/WorkOrders/Modals/ResolvePreventativeWorkOrderModal.vue';
import { isImpactAssessmentComplete } from '@/Pages/WorkOrders/Modals/impactAssessment.js';
import {
    isCaseCategoryComplete,
    isMsdNotificationComplete,
    isTroubleshootingComplete,
    visibleCaseCategories,
} from '@/Pages/WorkOrders/Modals/catsaExtras.js';
import { useWorkOrderSectionGates } from '@/composables/useWorkOrderSectionGates';
import { useInoperableReminder } from '@/composables/useInoperableReminder';
import InoperabilityReminderCheckbox from '@/Pages/WorkOrders/Modals/InoperabilityReminderCheckbox.vue';
import FileUpload from '@/Components/FileUpload.vue';
import InlineEditableField from '@/Pages/WorkOrders/Modals/InlineEditableField.vue';
import FileList from '@/Components/FileList.vue';
import Comments from '@/Components/Comments.vue';
import Alert from '@/Components/Alert.vue';
import Spinner from '@/Components/Spinner.vue';
import StepProgress from '@/Components/StepProgress.vue';
import { Deferred } from '@inertiaui/modal-vue';
import { replaceModal } from '@/Utils/visitModal';
import { eventBus, events } from '@/eventBus.js';
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue';
import { router, useForm } from '@inertiajs/vue3';
import { trans } from '@/mixins/i18n.js';
import { hasFeature } from '@/mixins/hasFeature.js';
import StatusBadge from '@/Components/StatusBadge.vue';
import AssetShortInfo from '@/Components/AssetShortInfo.vue';
import JetSelect from '@/Jetstream/Select.vue';
import Collapsable from '@/Components/Collapsable.vue';
import ExternalTicketsBadges from '@/Components/ExternalTicketsBadges.vue';
import WorkOrderActivityLog from '@/Pages/Modals/WorkOrder/WorkOrderActivityLog.vue';
import WorkOrderTag from '@/Components/CustomTags/WorkOrderTag.vue';
import { isMobile } from '@/Utils/UseMedia.js';

defineOptions({ inheritAttrs: false });

const modalRef = ref(null);
const resolvePreventativeModalRef = ref(null);

let externalTicketsPollInterval = null;

const props = defineProps({
    workOrder: {
        type: Object,
        // Defaulted (not required) so a transient render with no props -- which @inertiaui/modal
        // can trigger while the modal is opening/closing/tearing down -- cannot crash setup() by
        // reading props.workOrder.status etc. on undefined. The real modal render always receives
        // the work order from the server (WorkOrderController), and the nested ?. / || null
        // fallbacks throughout render an empty object harmlessly.
        default: () => ({}),
    },
    workOrderHistory: Array,
    externalTicketsEnabled: {
        type: Boolean,
        default: false,
    },
    externalTickets: {
        type: Array,
        default: null,
    },
    statuses: {
        type: [Object, null],
        required: true,
    },
    priorities: {
        type: [Object, null],
        required: true,
    },
    assignees: {
        type: [Array, null],
        required: true,
    },
    isEditable: {
        type: Boolean,
        required: true,
    },
    // Whether the current user may reopen this completed work order (admins/super-admins). Resolved
    // server-side from WorkOrderPolicy::moveToToDo / moveToInProgress, mirroring the board card flags.
    canReopen: {
        type: Boolean,
        default: false,
    },
    canUpdateAssetOperability: {
        type: Boolean,
        default: false,
    },
    processSteps: { type: Array, default: () => [] },
    workOrderPropertyConfig: {
        type: Object,
        default: () => ({}),
    },
    prefilledAssigneeId: {
        type: [String, null],
        default: null,
    },
    impactAssessment: { type: Object },
    catsaExtras: { type: Object },
    msdNotification: { type: Object },
    defaultContactPhone: { type: [String, null], default: null },
    caseCategories: { type: Array, default: () => [] },
    inoperableReminderKey: { type: [String, null], default: null },
    escalationInfo: { type: [Object, null], default: null },
});

const isDraft = computed(() => props.workOrder.status?.value === 'draft');
const isDone = computed(() => props.workOrder.status?.value === 'done');

// A real, server-provided work order always has an id. @inertiaui/modal can transiently render
// this component with no props (while opening/closing/tearing down); workOrder defaults to {} so
// setup() cannot crash, and this gate stops the body + title rendering child components with
// empty data on that throwaway pass.
const hasWorkOrder = computed(() => Boolean(props.workOrder?.id));
// A completed work order locks every field; only comments and attachments stay editable.
const fieldsEditable = computed(() => props.isEditable && !isDone.value);
const priorityEnabled = hasFeature('work_order_priority');

const assigneeUiConfig = computed(() => props.workOrderPropertyConfig?.assignees?.ui ?? {});
const isAssigneeRequired = computed(() => isDraft.value || (assigneeUiConfig.value.required ?? false));

const hasTransientTicket = computed(
    () =>
        props.externalTicketsEnabled &&
        Array.isArray(props.externalTickets) &&
        props.externalTickets.some((t) => t.bucket === 'provisioning' || t.bucket === 'syncing'),
);

const attachmentUiConfig = computed(() => props.workOrderPropertyConfig?.attachments?.ui ?? {});
const attachmentsRequired = computed(() => attachmentUiConfig.value.required ?? false);
const isImpactAssessmentMissing = computed(() => impactAssessmentEnabled.value && !isImpactAssessmentComplete(form.metadata.impact_assessment));
const isMsdNotificationMissing = computed(() => msdNotificationEnabled.value && !isMsdNotificationComplete(form.metadata.msd_notification));
const isTroubleshootingMissing = computed(() => troubleshootingEnabled.value && !isTroubleshootingComplete(form.metadata.troubleshooting));
const isCaseCategoryMissing = computed(
    () =>
        catsaExtrasEnabled.value &&
        !!form.metadata.case_category &&
        !isCaseCategoryComplete(form.metadata.case_category, form.metadata.case_subcategory),
);
const isRequiredPropertyMissing = computed(
    () =>
        (attachmentsRequired.value && !form.attachments?.length && !visibleAttachments.value?.length) ||
        isImpactAssessmentMissing.value ||
        isMsdNotificationMissing.value ||
        isTroubleshootingMissing.value ||
        isCaseCategoryMissing.value,
);

watch(
    hasTransientTicket,
    (isTransient) => {
        if (isTransient && !externalTicketsPollInterval) {
            externalTicketsPollInterval = setInterval(() => {
                modalRef.value?.reload({ only: ['externalTickets'] });
            }, 5000);
        } else {
            clearInterval(externalTicketsPollInterval);
            externalTicketsPollInterval = null;
        }
    },
    { immediate: true },
);

onUnmounted(() => {
    if (externalTicketsPollInterval) {
        clearInterval(externalTicketsPollInterval);
        externalTicketsPollInterval = null;
    }
});

const initialStatus = props.workOrder.status?.value;
const optimisticallyRemovedAttachmentIds = ref(new Set());

const visibleAttachments = computed(() => {
    const attachments = props.workOrder?.attachments ?? [];
    const removedIds = optimisticallyRemovedAttachmentIds.value;

    if (!attachments?.length || removedIds.size === 0) {
        return attachments;
    }

    return attachments.filter((attachment) => !removedIds.has(attachment.id));
});

const groupedAssignees = computed(() =>
    (props.assignees || []).map((assignee) => ({
        group: assignee.group,
        data: assignee.data.map((data) => ({
            label: data.name,
            value: data.id,
        })),
    })),
);

const form = useForm({
    status: props.workOrder.status?.value || null,
    priority: props.workOrder.priority?.value || 'medium',
    assignee: props.workOrder.assignee?.id || 'unassigned',
    startDate: props.workOrder.startDate || null,
    dueDate: props.workOrder.dueDate || null,
    serviceDate: props.workOrder.serviceDate || null,
    resolvedDate: props.workOrder.performedAt || null,
    mspRespondedAt: props.workOrder.mspRespondedAt || null,
    title: props.workOrder.title || null,
    description: props.workOrder.description || null,
    ticketId: props.workOrder.ticketId || null,
    ticketLink: props.workOrder.ticketLink || null,
    attachments: [],
    metadata: {
        impact_assessment: { ...(props.workOrder.impactAssessment ?? {}) },
        msd_notification: { ...(props.workOrder.msdNotification ?? {}) },
        troubleshooting: { ...(props.workOrder.troubleshooting ?? {}) },
        contact_phone: props.workOrder.contactPhone ?? props.defaultContactPhone ?? null,
        case_category: props.workOrder.caseCategory ?? null,
        case_subcategory: props.workOrder.caseSubcategory ?? null,
        inoperability_tag_acknowledged: props.workOrder.inoperabilityTagAcknowledged ?? false,
    },
});

const requiresResolvedDate = computed(
    () => props.workOrder.type?.value === 'preventative_maintenance' && hasFeature('schedule_preventative_maintenance_work_orders'),
);

const caseCategories = computed(() =>
    visibleCaseCategories(props.caseCategories, form.metadata.case_category, props.catsaExtras, props.canUpdateAssetOperability),
);

const { catsaExtrasEnabled, troubleshootingEnabled, msdNotificationEnabled, impactAssessmentEnabled } = useWorkOrderSectionGates({
    workOrderType: () => props.workOrder.type?.value,
    asset: () => props.workOrder.asset,
    caseCategory: () => form.metadata.case_category ?? null,
    catsaExtras: props.catsaExtras,
    msdNotification: props.msdNotification,
    impactAssessment: props.impactAssessment,
});

const { visible: inoperableReminderVisible } = useInoperableReminder({
    reminderKey: () => props.inoperableReminderKey,
    workOrderType: () => props.workOrder.type?.value,
    workOrderStatus: () => props.workOrder.status?.value,
    caseCategory: () => form.metadata.case_category ?? null,
    previousCaseCategory: () => props.workOrder.caseCategory ?? null,
    assetIsInoperable: () => props.workOrder.asset?.status?.operability === 'inoperable',
    catsaExtras: props.catsaExtras,
});

const inoperabilityAckMissing = computed(() => inoperableReminderVisible.value && !form.metadata.inoperability_tag_acknowledged);

const isCaseCategoryEditable = computed(() => props.isEditable && form.status === 'in_progress' && form.assignee && form.assignee !== 'unassigned');

// A completed work order is otherwise read-only, but a privileged user (admin/super-admin) may
// reopen it (props.canReopen, resolved server-side). Only the status field unlocks; every other
// field stays frozen.
const statusChanged = computed(() => form.status !== initialStatus);

const isSaveDisabled = computed(() => {
    if (uploadInProgress.value) {
        return true;
    }

    // Completed WOs only accept new attachments — unless a privileged user is reopening them.
    if (isDone.value) {
        return !statusChanged.value && !form.attachments?.length;
    }

    return (isDraft.value ? form.assignee === 'unassigned' : !form.isDirty) || isRequiredPropertyMissing.value || inoperabilityAckMissing.value;
});

watch(
    () => form.assignee,
    (newAssignee) => {
        modalRef.value?.reload({
            only: ['workOrderPropertyConfig'],
            data: { assignee: newAssignee },
        });
    },
);

const error = ref(null);
const commentDraft = ref(null);
const showCommentWarning = ref(false);
const uploadInProgress = ref(false);
const isEscalating = ref(false);

watch(
    () => props.workOrder,
    () => {
        // Once the server-refreshed work order arrives, trust it and drop any optimistic filtering.
        optimisticallyRemovedAttachmentIds.value = new Set();
    },
);

const clearWorkOrderFromUrl = () => {
    const url = new URL(window.location.href);
    url.searchParams.delete('workorder');
    window.history.replaceState({}, '', url.toString());
};

const closeModal = () => {
    clearWorkOrderFromUrl();
    modalRef.value?.close();
};

const onEscalate = () => {
    if (!props.workOrder.id || !props.isEditable || !props.escalationInfo?.nextAssigneeId || isEscalating.value) {
        return;
    }

    // With unsaved edits, only stage the assignee into the form so the normal Save persists every
    // change together — escalating outright here would silently drop those edits.
    if (form.isDirty) {
        form.assignee = props.escalationInfo.nextAssigneeId;

        return;
    }

    // Clean work order: one-click escalate via the dedicated endpoint — assigns the next-level
    // person (bypassing the full-form validation), then closes the modal. Guard against rapid
    // repeated clicks queueing multiple requests and advancing more than one level.
    isEscalating.value = true;
    router.put(
        route('workorders.escalate', props.workOrder.id),
        {},
        {
            preserveScroll: true,
            onFinish: () => {
                isEscalating.value = false;
            },
            onSuccess: (page) => {
                // Escalation can fail gracefully (stale path/pool) — the server flashes an error and
                // keeps the modal open; don't signal a save or close in that case.
                if (page.props?.flash?.error) {
                    return;
                }

                eventBus.$emit(events.workOrderUpdated, props.workOrder.id);
                modalRef.value?.emit('saved');
                clearWorkOrderFromUrl();
                modalRef.value?.close();
            },
        },
    );
};

const submitForm = () => {
    if (!props.workOrder.id || !props.isEditable) {
        return;
    }

    if (form.status === 'done' && requiresResolvedDate.value && !form.resolvedDate) {
        resolvePreventativeModalRef.value?.open({
            workOrder: props.workOrder,
            onConfirm: (resolvedDate) => {
                form.resolvedDate = resolvedDate;
                performSubmit();
            },
        });

        return;
    }

    performSubmit();
};

const performSubmit = () => {
    form.transform((data) => {
        if (!data.attachments) {
            delete data.attachments;
        }

        if (!priorityEnabled) {
            delete data.priority;
        }

        if (data.status !== 'done' || !data.resolvedDate) {
            delete data.resolvedDate;
        }

        return data;
    }).put(route('workorders.update', props.workOrder.id), {
        preserveScroll: true,
        onSuccess: () => {
            eventBus.$emit(events.workOrderUpdated, props.workOrder.id);

            if (initialStatus !== form.status) {
                eventBus.$emit(events.workOrderStatusUpdated, {
                    workOrderId: props.workOrder.id,
                    status: form.status,
                });
            }

            modalRef.value?.emit('saved');
            clearWorkOrderFromUrl();
            modalRef.value?.close();
        },
    });
};

const updateComment = (comment) => {
    commentDraft.value = comment;
    if (!commentDraft.value) {
        showCommentWarning.value = false;
    }
};
const ensureCommentPosted = (callback) => {
    if (!commentDraft.value) {
        callback();
        return;
    }

    if (showCommentWarning.value) {
        nextTick(() => {
            callback();
        });
    }

    showCommentWarning.value = !!commentDraft.value;
};

function handleDeletedAttachment(deletedFile) {
    if (deletedFile?.id) {
        const next = new Set(optimisticallyRemovedAttachmentIds.value);
        next.add(deletedFile.id);
        optimisticallyRemovedAttachmentIds.value = next;
    }

    reloadWorkOrder();
}

function reloadWorkOrder() {
    modalRef.value?.reload({
        only: ['workOrder', 'workOrderPropertyConfig'],
        data: { assignee: form.assignee },
    });
}

const openTask = (url) => {
    clearWorkOrderFromUrl();
    replaceModal(modalRef.value, url);
};

onMounted(() => {
    if (!props.workOrder.assignee?.id && props.prefilledAssigneeId) {
        form.assignee = props.prefilledAssigneeId;
    }
});
</script>
